import { useEffect, useState } from "react";

/**
 * useTxList
 * ---------
 * Manages the full lifecycle of a multi-transaction list inside any section.
 *
 * @param {object}   state      - section state slice from parent (must have `transactions`, `paidAmounts`)
 * @param {function} setState   - parent setState for that slice
 * @param {function} initFn     - factory that returns a fresh empty transaction (e.g. pcInit, labInit)
 *
 * Returns everything a section shell needs:
 *   transactions, paidAmounts,
 *   formState, showingNewForm, editingId, editingTx,
 *   openNew, cancelNew, startEdit, cancelEdit,
 *   saveTx, removeTx, setPaid
 */
export function useTxList(state, setState, initFn) {
  // formState:
  //   null      → blank new-form visible (initial state)
  //   "hidden"  → no form, only bars + "Add Another" button
  //   "<id>"    → editing that transaction inline
  const [formState, setFormState] = useState(null);

  const transactions = state.transactions || [];
  const paidAmounts = state.paidAmounts || {};

  const showingNewForm = formState === null;
  const editingId =
    formState !== null && formState !== "hidden" ? formState : null;
  const editingTx = editingId
    ? transactions.find((t) => t.id === editingId)
    : null;

  useEffect(() => {
    if (transactions.length > 0) {
      setFormState("hidden");
    } else {
      setFormState(null);
    }
  }, [transactions]);
  const openNew = () => setFormState(null);
  const cancelNew = () => setFormState("hidden");
  const startEdit = (id) => setFormState(id);
  const cancelEdit = () => setFormState("hidden");

  const saveTx = (tx) => {
    const id = Date.now().toString();
    if (editingId) {
      setState((p) => ({
        ...p,
        transactions: p.transactions.map((t) =>
          t.id === editingId ? { ...tx, id: editingId } : t,
        ),
      }));
    } else {
      setState((p) => ({
        ...p,
        transactions: [...(p.transactions || []), { ...tx, id }],
      }));
    }
    setFormState("hidden");
  };

  const removeTx = (id) => {
    setState((p) => {
      const { [id]: _, ...rest } = p.paidAmounts || {};
      return {
        ...p,
        transactions: p.transactions.filter((t) => t.id !== id),
        paidAmounts: rest,
      };
    });
    if (editingId === id) setFormState("hidden");
  };

  const setPaid = (id) => (v) =>
    setState((p) => ({
      ...p,
      paidAmounts: { ...(p.paidAmounts || {}), [id]: v },
    }));

  const emptyTx = () => ({
    ...initFn(),
    id: Date.now().toString() + Math.random(),
  });

  return {
    // data
    transactions,
    paidAmounts,
    editingId,
    editingTx,
    showingNewForm,
    formState,
    emptyTx,
    // actions
    openNew,
    cancelNew,
    startEdit,
    cancelEdit,
    saveTx,
    removeTx,
    setPaid,
  };
}
